<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

// Templates de regras comuns para o Brasil
const PRESETS = [
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_EMERGENCY'),
    pattern: '_19X',
    strip: 0,
    prepend: '',
  },
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_LOCAL_8'),
    pattern: '_XXXXXXXX',
    strip: 0,
    prepend: '',
  },
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_LOCAL_9'),
    pattern: '_9XXXXXXXX',
    strip: 0,
    prepend: '',
  },
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_NATIONAL'),
    pattern: '_0XXXXXXXXX',
    strip: 1,
    prepend: '+55',
  },
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_NATIONAL_9'),
    pattern: '_0XX9XXXXXXXX',
    strip: 1,
    prepend: '+55',
  },
  {
    name: t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESET_INTL'),
    pattern: '_00X.',
    strip: 2,
    prepend: '+',
  },
];

const showPresets = ref(false);
const previewNumber = ref('');

function rules() {
  return props.modelValue;
}

function update(newRules) {
  emit('update:modelValue', newRules);
}

function addRule() {
  update([
    ...rules(),
    {
      id: crypto.randomUUID(),
      name: '',
      pattern: '',
      strip: 0,
      prepend: '',
      enabled: true,
    },
  ]);
}

function addPreset(preset) {
  update([...rules(), { id: crypto.randomUUID(), ...preset, enabled: true }]);
  showPresets.value = false;
}

function removeRule(id) {
  update(rules().filter(r => r.id !== id));
}

function updateRule(id, field, value) {
  update(rules().map(r => (r.id === id ? { ...r, [field]: value } : r)));
}

function toggleEnabled(id) {
  update(rules().map(r => (r.id === id ? { ...r, enabled: !r.enabled } : r)));
}

function matchPattern(pattern, number) {
  if (!pattern) return false;
  // Converte padrão Asterisk para regex simples
  const re = pattern
    .replace(/^_/, '')
    .replace(/X/g, '[0-9]')
    .replace(/Z/g, '[1-9]')
    .replace(/N/g, '[2-9]')
    .replace(/\./g, '[0-9]+')
    .replace(/!/g, '[0-9]*');
  return new RegExp(`^${re}$`).test(number);
}

// Simula como o número seria discado com as regras ativas
function previewDial(number) {
  if (!number) return '—';
  const matched = rules()
    .filter(r => r.enabled)
    .find(rule => matchPattern(rule.pattern, number));
  if (matched) {
    const stripped = number.slice(matched.strip || 0);
    return `${matched.prepend || ''}${stripped}`;
  }
  return t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PREVIEW_NO_MATCH');
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Cabeçalho -->
    <div class="flex items-center justify-between">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-0.5">
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.HINT') }}
        </p>
      </div>
      <div class="relative flex gap-2">
        <button
          type="button"
          class="flex items-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
          @click="showPresets = !showPresets"
        >
          <span class="i-lucide-layout-list size-3.5" />
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESETS') }}
        </button>
        <!-- Dropdown de presets -->
        <div
          v-if="showPresets"
          class="absolute right-0 top-8 z-20 w-60 rounded-xl border border-n-weak bg-n-background shadow-lg overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-3 py-2 border-b border-n-weak"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{
                t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PRESETS_TITLE')
              }}
            </span>
            <button
              type="button"
              class="text-n-slate-9 hover:text-n-slate-11"
              @click="showPresets = false"
            >
              <span class="i-lucide-x size-3.5" />
            </button>
          </div>
          <ul class="py-1">
            <li
              v-for="preset in PRESETS"
              :key="preset.name"
              class="flex items-center justify-between px-3 py-2 hover:bg-n-alpha-1 cursor-pointer"
              @click="addPreset(preset)"
            >
              <div>
                <p class="text-xs font-medium text-n-slate-12">
                  {{ preset.name }}
                </p>
                <p class="text-[11px] font-mono text-n-slate-10">
                  {{ preset.pattern }}
                </p>
              </div>
              <span class="i-lucide-plus size-3.5 text-n-slate-9" />
            </li>
          </ul>
        </div>

        <button
          type="button"
          class="flex items-center gap-1 rounded-lg bg-n-brand/10 px-2.5 py-1.5 text-xs text-n-brand hover:bg-n-brand/20 transition-colors"
          @click="addRule"
        >
          <span class="i-lucide-plus size-3.5" />
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.ADD') }}
        </button>
      </div>
    </div>

    <!-- Lista de regras -->
    <div v-if="modelValue.length > 0" class="flex flex-col gap-2">
      <!-- Cabeçalho da tabela -->
      <div class="grid grid-cols-[1fr_1fr_5rem_1fr_2rem_2rem] gap-2 px-1">
        <span
          class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wide"
        >
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.COL_NAME') }}
        </span>
        <span
          class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wide"
        >
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.COL_PATTERN') }}
        </span>
        <span
          class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wide"
        >
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.COL_STRIP') }}
        </span>
        <span
          class="text-[11px] font-medium text-n-slate-9 uppercase tracking-wide"
        >
          {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.COL_PREPEND') }}
        </span>
        <span />
        <span />
      </div>

      <div
        v-for="rule in modelValue"
        :key="rule.id"
        class="grid grid-cols-[1fr_1fr_5rem_1fr_2rem_2rem] gap-2 items-center rounded-lg border border-n-weak px-3 py-2"
        :class="{ 'opacity-50': !rule.enabled }"
      >
        <input
          :value="rule.name"
          type="text"
          :placeholder="
            t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.NAME_PH')
          "
          class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12 outline-none focus:border-n-brand w-full"
          @input="updateRule(rule.id, 'name', $event.target.value)"
        />
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <input
          :value="rule.pattern"
          type="text"
          placeholder="_XXXXXXXX"
          class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 font-mono text-xs text-n-slate-12 outline-none focus:border-n-brand w-full"
          @input="updateRule(rule.id, 'pattern', $event.target.value)"
        />
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        <input
          :value="rule.strip"
          type="number"
          min="0"
          max="9"
          class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-12 outline-none focus:border-n-brand w-full"
          @input="updateRule(rule.id, 'strip', Number($event.target.value))"
        />
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <input
          :value="rule.prepend"
          type="text"
          placeholder="+55"
          class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 font-mono text-xs text-n-slate-12 outline-none focus:border-n-brand w-full"
          @input="updateRule(rule.id, 'prepend', $event.target.value)"
        />
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        <!-- Toggle ativo -->
        <button
          type="button"
          :title="rule.enabled ? 'Desativar' : 'Ativar'"
          @click="toggleEnabled(rule.id)"
        >
          <span
            :class="
              rule.enabled
                ? 'i-lucide-toggle-right text-n-brand'
                : 'i-lucide-toggle-left text-n-slate-9'
            "
            class="size-5"
          />
        </button>
        <!-- Remover -->
        <button
          type="button"
          class="text-n-slate-9 hover:text-red-500 transition-colors"
          @click="removeRule(rule.id)"
        >
          <span class="i-lucide-trash-2 size-3.5" />
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-n-weak py-6 text-center"
    >
      <span class="i-lucide-phone-forwarded size-6 text-n-slate-8" />
      <p class="text-xs text-n-slate-10">
        {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.EMPTY') }}
      </p>
    </div>

    <!-- Preview de discagem -->
    <div class="rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2.5">
      <p class="mb-1.5 text-xs font-medium text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.VOICE.ASTERISK.DIALING_RULES.PREVIEW_TITLE') }}
      </p>
      <div class="flex items-center gap-2">
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <input
          v-model="previewNumber"
          type="tel"
          placeholder="11999990000"
          class="flex-1 rounded-md border border-n-weak bg-n-background px-2 py-1 text-xs text-n-slate-12 outline-none focus:border-n-brand"
        />
        <!-- eslint-enable vue/no-bare-strings-in-template -->
        <span class="i-lucide-arrow-right size-3.5 text-n-slate-9" />
        <span class="font-mono text-xs text-n-brand font-medium min-w-[8rem]">
          {{ previewDial(previewNumber) }}
        </span>
      </div>
      <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
      <p class="mt-1.5 text-[11px] text-n-slate-9">
        X=0-9 · Z=1-9 · N=2-9 · .=um ou mais · !=zero ou mais
      </p>
    </div>
  </div>
</template>
